import { Box, AspectRatio } from "@mantine/core";

interface GenericEmbedProps {
  url?: string;
  title?: string;
}

interface Resolved {
  src: string;
  aspect?: number;
  height?: number;
  allow?: string;
}

function resolve(url: string): Resolved | null {
  // Vimeo
  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeo) return { src: `https://player.vimeo.com/video/${vimeo[1]}`, aspect: 16 / 9 };

  // Loom
  const loom = url.match(/loom\.com\/share\/([\w-]+)/);
  if (loom) return { src: `https://www.loom.com/embed/${loom[1]}`, aspect: 16 / 9 };

  // Spotify (track, album, playlist, episode, show, artist)
  const spotify = url.match(/open\.spotify\.com\/(track|album|playlist|episode|show|artist)\/([\w-]+)/);
  if (spotify) return { src: `https://open.spotify.com/embed/${spotify[1]}/${spotify[2]}`, height: 232 };

  // CodePen
  const codepen = url.match(/codepen\.io\/([\w-]+)\/(?:pen|full|details)\/([\w-]+)/);
  if (codepen) return { src: `https://codepen.io/${codepen[1]}/embed/${codepen[2]}?default-tab=result`, aspect: 16 / 9 };

  // Twitch clip
  const twitchClip = url.match(/clips\.twitch\.tv\/(\w+)/);
  if (twitchClip && typeof window !== "undefined") {
    return { src: `https://clips.twitch.tv/embed?clip=${twitchClip[1]}&parent=${window.location.hostname}`, aspect: 16 / 9 };
  }

  // Google Slides
  const gslides = url.match(/docs\.google\.com\/presentation\/d\/([\w-]+)/);
  if (gslides) return { src: `https://docs.google.com/presentation/d/${gslides[1]}/embed`, aspect: 16 / 9 };

  // Fallback: iframe the URL directly (works for many "just embed the page" cases)
  return { src: url, aspect: 16 / 9 };
}

export function GenericEmbed({ url, title }: GenericEmbedProps) {
  if (!url) return null;
  const resolved = resolve(url);
  if (!resolved) return null;

  const iframe = (
    <iframe
      src={resolved.src}
      title={title ?? "Embed"}
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
      allowFullScreen
      style={{ border: "none", borderRadius: 12, width: "100%", height: "100%" }}
    />
  );

  return (
    <Box my="xl">
      {resolved.aspect ? (
        <AspectRatio ratio={resolved.aspect}>{iframe}</AspectRatio>
      ) : (
        <Box style={{ height: resolved.height ?? 400 }}>{iframe}</Box>
      )}
    </Box>
  );
}
